import FeatureLayer from '@arcgis/core/layers/FeatureLayer'
import ImageryLayer from '@arcgis/core/layers/ImageryLayer'
import TileLayer from '@arcgis/core/layers/TileLayer.js'
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer'

import { DEFAULT_LAYER_OPACITY, MAP_LAYER_DEFAULT_PRIORITIES, MAP_LAYER_IDS } from './constants'
import { kebabify, sortList, toTitleCase } from '../lib/utils'
import ghAnalytics from '../lib/analytics'

/*
 * The map.layers stack, bottom (index 0) to top:
 *
 *   [historical imagery] [contour] <operational band> [gohunt-* UI] [terrain analysis]
 *
 * Basemaps and hillshade are NOT in this collection — they live in map.basemap.baseLayers
 * (see MapInitializer). Terrain analysis is appended last by MapInitializer, so it sits
 * above the UI layers rather than below the band.
 *
 * The operational band — state layers plus reference lines/labels — is the only region we
 * order, and it is kept sorted descending by priority: higher number = lower index = drawn
 * at the bottom. Reference lines (100) and labels (0) are ordinary members of the band and
 * sort on their own priority like everything else.
 */

/**
 * UI / graphics layers that must stay above every operational layer. Marks the top of the band.
 */
const TOP_TIER_IDS = new Set([
  MAP_LAYER_IDS.content,
  MAP_LAYER_IDS.sketch,
  MAP_LAYER_IDS.search,
  MAP_LAYER_IDS.select,
  MAP_LAYER_IDS.ghost
])

/**
 * Layers pinned below the band, contiguous from index 0. Historical imagery is managed by
 * historicalImagery.js, whose removeLayers/setNewLayers scan from index 0 and break at the
 * first non-historical layer — inserting anything at index 0 would strand those layers on
 * the map. Contour is a static content layer that belongs under the band.
 */
const PINNED_BOTTOM_IDS = new Set([MAP_LAYER_IDS.historical, MAP_LAYER_IDS.contour])

/**
 * Helper functions to check if a category, subCategory, or layer has layers, categories, or subCategories
 */
export const hasLayers = category => category.layers?.length > 0
/**
 * Helper function to check if a category has categories
 */
export const hasCategories = category => category.categories?.length > 0
/**
 * Helper function to check if a category has subCategories
 */
export const hasSubCategories = category => category.subCategories?.length > 0
/**
 * Helper function to set the visibility of base layers
 */
export const setBaseLayersVisiblity = ({ all, baseLayers, ids, visible }) => {
  for (let i = 0; i < baseLayers.length; i++) {
    const baseLayer = baseLayers[i]
    baseLayer.visible = all ? visible : ids.includes(baseLayer.id)
  }
}
/**
 * Helper function to set the layer manifest
 */
export const setLayerManifest = (layers, preferences) => {
  const { special_events, states } = layers
  let newEventsList = []
  let newStatesList = []

  if (special_events?.length > 0) {
    newEventsList = sortAndSetLayers(special_events, preferences)
  }
  if (states?.length > 0) {
    newStatesList = sortAndSetLayers(states, preferences)
  }
  return {
    ...layers,
    events: sortList(newEventsList, 'stateName', 'string'),
    states: sortList(newStatesList, 'stateName', 'string')
  }
}
/**
 * Helper function to set the layer priority map
 */
export const setLayerPriorityMap = ({ states = [] }) => {
  const layerPriorityMap = {}

  const sl = states.length
  for (let i = 0; i < sl; i++) {
    const { stateName, categories } = states[i]
    const stateMap = {}

    const cl = categories.length
    for (let j = 0; j < cl; j++) {
      const { categoryName, subCategories } = categories[j]

      const scl = subCategories.length
      for (let k = 0; k < scl; k++) {
        const { subCategoryName, layers } = subCategories[k]

        const ll = layers.length
        for (let l = 0; l < ll; l++) {
          const { layerName, priority } = layers[l]
          const key = kebabify(`${stateName} ${categoryName} ${subCategoryName} ${layerName}`)
          stateMap[key] = priority
        }
      }
    }
    layerPriorityMap[stateName] = stateMap
  }

  return layerPriorityMap
}
/**
 * Helper function to set the operational layers
 */
export const setOperationalLayers = ({ basemaps, contentConfig }) => {
  const baseLayers = []
  const contentLayers = []

  for (let basemapConfig of basemaps) {
    let basemapLayer = null
    const baseLayerId = MAP_LAYER_IDS.basemap[basemapConfig.name.toLowerCase()]
    const basemapLayerParams = {
      apiKey: basemapConfig.apiKey,
      id: baseLayerId,
      priority: basemapConfig.priority,
      url: basemapConfig.tileServiceUrl,
      visible: true
    }
    try {
      if (baseLayerId !== MAP_LAYER_IDS.basemap.topographic) {
        basemapLayer = new TileLayer(basemapLayerParams)
      } else {
        basemapLayer = new VectorTileLayer(basemapLayerParams)
      }
    } catch (error) {
      console.error(toTitleCase(baseLayerId.replace(/-/g, ' ')) + ' retrieval error: ', error)
    }
    if (basemapLayer) {
      if (basemapConfig.default) {
        baseLayers.push(basemapLayer)
      } else {
        baseLayers.unshift(basemapLayer)
      }
    }
  }

  let basemapHillshadeLayer = null
  try {
    basemapHillshadeLayer = new TileLayer({
      id: MAP_LAYER_IDS.hillshade,
      priority: contentConfig.hillshade.priority,
      url: contentConfig.hillshade.url,
      visible: false
    })
  } catch (error) {
    console.error(`${toTitleCase(MAP_LAYER_IDS.hillshade.replace(/-/g, ' '))} retrieval error: `, error)
  }

  if (basemapHillshadeLayer) baseLayers.unshift(basemapHillshadeLayer)

  // Prefer web-specific VTLs; fall back to the shared keys until every env has _web.
  const referenceLabels = contentConfig.reference_labels_web ?? contentConfig.reference_labels
  const referenceLines = contentConfig.reference_lines_web ?? contentConfig.reference_lines

  const contentLayerParams = [
    {
      id: MAP_LAYER_IDS.contour,
      priority: contentConfig.contour.priority ?? MAP_LAYER_DEFAULT_PRIORITIES.contour,
      url: contentConfig.contour.url,
      visible: false
    },
    {
      id: MAP_LAYER_IDS.referenceLabels,
      priority: referenceLabels?.priority ?? MAP_LAYER_DEFAULT_PRIORITIES.referenceLabels,
      url: referenceLabels?.url
    },
    {
      id: MAP_LAYER_IDS.referenceLines,
      priority: referenceLines?.priority ?? MAP_LAYER_DEFAULT_PRIORITIES.referenceLines,
      url: referenceLines?.url
    }
  ]
  contentLayerParams.sort((a, b) => b.priority - a.priority)

  for (const layerParams of contentLayerParams) {
    let newContentLayer = null
    try {
      newContentLayer = new VectorTileLayer(layerParams)
    } catch (error) {
      console.error(`${toTitleCase(layerParams.id.replace(/-/g, ' '))} retrieval error: `, error)
    }
    if (newContentLayer) contentLayers.push(newContentLayer)
  }

  return {
    baseLayers,
    contentLayers
  }
}
/**
 * Helper function to set the map layers
 */
export const setMapLayers = ({ events = [], mapRef, priorityMap = {}, states = [] }) => {
  const layerSets = [events, states]
  const result = { added: [], errored: [], removed: [] }
  const allLayersAdded = []

  // Outer: for each group (events & states)
  const setsLength = layerSets.length
  for (let setIndex = 0; setIndex < setsLength; setIndex++) {
    const layersGroup = layerSets[setIndex]

    // Middle: for each state-like object
    const lgl = layersGroup.length
    for (let s = 0; s < lgl; s++) {
      const state = layersGroup[s]
      const statePriorityMap = priorityMap[state.stateName] || {}

      // For every category → subCategory → layer
      const { categories } = state
      const cl = categories.length
      for (let c = 0; c < cl; c++) {
        const category = categories[c]
        const { categoryName, opacity, subCategories = [] } = category

        if (!subCategories.length) continue

        const scl = subCategories.length
        for (let sc = 0; sc < scl; sc++) {
          const subCategory = subCategories[sc]
          const { subCategoryName, layers = [] } = subCategory

          if (!layers.length) continue

          const ll = layers.length
          for (let l = 0; l < ll; l++) {
            const layer = layers[l]
            if (!layer.control?.enabled) continue

            const layerId = `${state.stateName}-${categoryName}-${subCategoryName}-${layer.layerName}`

            // Already on the map (re-run of setMapLayers, or a duplicate manifest entry) —
            // a second instance with the same id shadows the first in findLayerById and
            // double-renders it.
            if (mapRef.findLayerById(layerId)) continue

            const layerPriority = statePriorityMap[kebabify(layerId)]

            // Create and attach error handler
            const newLayer = createNewLayer({
              id: layerId,
              opacity,
              priority: layerPriority,
              urls: layer.urls
            })

            newLayer.on('layerview-create-error', e => {
              ghAnalytics.logLayerError(e, state, category, subCategory, layer)
              result.errored.push(`${state.stateName}|${categoryName}|${subCategoryName}|${layer.layerName}`)
            })

            // If no error event fires synchronously, we assume success
            allLayersAdded.push(newLayer)
            result.added.push(`${state.stateName}|${categoryName}|${subCategoryName}|${layer.layerName}`)
          }
        }
      }
    }
  }

  if (allLayersAdded.length > 0) {
    orderLayers(mapRef, allLayersAdded)
  }

  return result
}
/**
 * Helper function to update the map layers
 */
export const updateMapLayers = ({ all, layers, mapRef, priorityMap, status }) => {
  const layerKeys = []
  const newLayers = []

  // Process layers directly
  for (const layer of layers) {
    const { categoryName, layerName, stateName, subCategoryName, control } = layer
    const baseIdValues = [stateName, categoryName, subCategoryName, layerName]
    const isVisible = all ? status : control?.enabled || false
    const layerId = baseIdValues.join('-')
    const layerKey = baseIdValues.join('|')
    const layerPriority = priorityMap[stateName]?.[kebabify(layerId)] ?? 1
    const mapLayer = mapRef.findLayerById(layerId)

    // Already on the map — toggling only flips visibility. The layer and its slot in the
    // stack are left alone, so turning it back on never re-fetches or re-orders it.
    if (mapLayer) {
      mapLayer.visible = isVisible
      layerKeys.push(layerKey)
      continue
    }

    const newMapLayer = createNewLayer({
      id: layerId,
      opacity: layer.opacity,
      priority: layerPriority,
      urls: layer.urls,
      visible: isVisible
    })

    newMapLayer?.on('layerview-create-error', e => {
      ghAnalytics.logLayerError(e, { stateName }, { categoryName }, { subCategoryName }, layer)
    })

    if (newMapLayer?.id) {
      layerKeys.push(layerKey)
      newLayers.push(newMapLayer)
    }
  }

  if (newLayers.length > 0) {
    orderLayers(mapRef, newLayers)
  }

  return layerKeys
}
/**
 * Helper function to update the map layers opacity
 */
export const updateMapLayersOpacity = ({ layerIds, opacity, mapRef }) => {
  for (const layerId of layerIds) {
    const mapLayer = mapRef.findLayerById(layerId)
    if (mapLayer) {
      mapLayer.opacity = opacity
    }
  }
}

/*******************
 * Helper functions
 ******************/

/**
 * Helper function to add layer controls
 */
function addLayerControls(stateObj, category, subCategory, preferences) {
  if (!subCategory || subCategory == null) {
    return null
  }
  return subCategory.layers.map(layer => {
    const compositeKey = `${stateObj.stateName}|${category.categoryName}|${subCategory.subCategoryName}|${layer.layerName}`
    return {
      ...layer,
      control: {
        enabled: preferences.active.some(key => key === compositeKey)
      }
    }
  })
}

/**
 * Helper function to create a new layer
 */
function createNewLayer({ id, opacity = DEFAULT_LAYER_OPACITY, outFields = [], priority, urls, visible = true }) {
  const { raster, vectorTile, mainRes } = urls
  let newLayer = null
  let params = { id, opacity, priority, visible }
  if (raster != null) {
    params = {
      ...params,
      url: raster
    }
    newLayer = new ImageryLayer(params)
  } else if (vectorTile != null) {
    params = {
      ...params,
      url: vectorTile
    }
    newLayer = new VectorTileLayer(params)
  } else {
    params = {
      ...params,
      elevationInfo: { mode: 'on-the-ground' },
      outFields,
      url: mainRes
    }
    newLayer = new FeatureLayer(params)
  }
  return newLayer
}

function isTopTierLayer(layer) {
  if (!layer?.id) return false
  if (TOP_TIER_IDS.has(layer.id)) return true
  // Overlays use ids like gohunt-overlay-...
  return typeof layer.id === 'string' && layer.id.startsWith(`${MAP_LAYER_IDS.overlay}-`)
}

function isPinnedBottomLayer(layer) {
  return !!layer?.id && PINNED_BOTTOM_IDS.has(layer.id)
}

/**
 * Priority that governs a layer's z-position. Every manifest layer carries one; this only
 * guards against a missing/NaN value poisoning the comparator — NaN makes a sort
 * non-deterministic for the whole batch, not just the offending layer.
 */
function resolvePriority(layer) {
  const priority = layer?.priority
  return typeof priority === 'number' && !Number.isNaN(priority) ? priority : 0
}

/**
 * Slot for one new layer in the band. Scans past the pinned-bottom block, then returns the
 * index of the first layer with a strictly lower priority — so the new layer lands above
 * existing layers of equal priority (newest draws on top). Stops at the UI band.
 */
function getLayerInsertIndex(layerItems, newMapLayer) {
  const newPriority = resolvePriority(newMapLayer)

  let i = 0
  while (i < layerItems.length && isPinnedBottomLayer(layerItems[i])) {
    i++
  }

  for (; i < layerItems.length; i++) {
    const current = layerItems[i]
    if (isTopTierLayer(current)) return i
    if (resolvePriority(current) < newPriority) return i
  }

  return layerItems.length
}

/**
 * Top of the operational band — the first UI layer, or the end of the collection when no
 * UI layers are on the map yet.
 */
function getBandTopIndex(mapRef) {
  const items = mapRef.layers.items
  for (let i = 0; i < items.length; i++) {
    if (isTopTierLayer(items[i])) return i
  }
  return items.length
}

/**
 * Move reference-labels back to the top of the band via remove + add of the SAME instance
 * (no destroy, no remount of any other layer). Re-adding rebuilds only the labels layer view,
 * on top of the operational VTLs — the mechanism that makes cold-load ordering hold in prod.
 */
function reseatReferenceLabels(mapRef) {
  const labels = mapRef.findLayerById(MAP_LAYER_IDS.referenceLabels)
  if (!labels || mapRef.layers.indexOf(labels) === -1) return

  mapRef.remove(labels)
  mapRef.add(labels, getBandTopIndex(mapRef))
}

/**
 * Place new layers into the band by priority. Used by both cold load and toggles — the band
 * is kept sorted, so each layer only has to find its own slot, and nothing already on the map
 * moves or rebuilds its layer view. Sorting the batch first keeps equal priorities
 * deterministic (later insert draws on top).
 */
function orderLayers(mapRef, layers) {
  const ordered = layers.slice().sort((a, b) => resolvePriority(b) - resolvePriority(a))

  for (let i = 0; i < ordered.length; i++) {
    mapRef.add(ordered[i], getLayerInsertIndex(mapRef.layers.items, ordered[i]))
  }

  reseatReferenceLabels(mapRef)
}

/**
 * Helper function to sort and set the layers
 */
function sortAndSetLayers(layersArray, preferences) {
  const defaultOpacityMap = preferences.category.opacity

  return layersArray.map(layer => {
    if (!hasCategories(layer)) {
      return layer
    }
    sortList(layer.categories, 'categoryName', 'string')
    const newCategories = []
    const baseStateName = layer.stateName

    for (let i = 0; i < layer.categories.length; i++) {
      const category = { ...layer.categories[i] }

      // Ensure isPublished exists
      if (!category.isPublished) category.isPublished = true

      // Process sub‑categories
      if (Array.isArray(category.subCategories) && category.subCategories.length) {
        const newSubCategories = []

        for (let j = 0; j < category.subCategories.length; j++) {
          const subCategory = category.subCategories[j]
          let newLayers = []

          if (hasLayers(subCategory)) {
            newLayers = addLayerControls(layer, category, subCategory, preferences) || []

            if (newLayers.length) sortList(newLayers, 'layerName', 'string')
          }

          newSubCategories.push({
            ...subCategory,
            layers: newLayers
          })
        }

        category.subCategories = newSubCategories
      }

      // Set opacity
      const key = `${baseStateName}-${category.categoryName}`
      category.opacity = defaultOpacityMap[key] || DEFAULT_LAYER_OPACITY

      newCategories.push(category)
    }

    return {
      ...layer,
      categories: newCategories
    }
  })
}
